import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import Layout from './components/Layout/Layout';

// Route-level code splitting: each page ships as its own chunk so the initial
// bundle (and mobile load) stays small. Heavy pages like Products/Gallery only
// download when visited.
const Home = React.lazy(() => import('./pages/Home'));
const About = React.lazy(() => import('./pages/About'));
const ServicesPage = React.lazy(() => import('./pages/ServicesPage'));
const ServiceDetail = React.lazy(() => import('./pages/ServiceDetail'));
const ContactPage = React.lazy(() => import('./pages/ContactPage'));
const ProductCatalog = React.lazy(() => import('./pages/ProductCatalog'));
const GalleryPage = React.lazy(() => import('./pages/GalleryPage'));

const PageFallback: React.FC = () => (
  <div style={{ minHeight: '70vh' }} aria-busy="true" aria-live="polite" />
);

const ScrollToHash: React.FC = () => {
  const { pathname, hash } = useLocation();

  React.useEffect(() => {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
  }, []);

  React.useEffect(() => {
    if (!hash) {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      return;
    }

    const id = hash.replace('#', '');
    window.requestAnimationFrame(() => {
      const section = document.getElementById(id);
      if (section) {
        section.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });
  }, [pathname, hash]);

  return null;
};

const App: React.FC = () => (
  <Router>
    <ScrollToHash />
    <Layout>
      <Suspense fallback={<PageFallback />}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/services" element={<ServicesPage />} />
          <Route path="/services/:slug" element={<ServiceDetail />} />
          <Route path="/products" element={<ProductCatalog />} />
          <Route path="/products/:category" element={<ProductCatalog />} />
          <Route path="/gallery" element={<GalleryPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </Layout>
  </Router>
);

export default App;
